/**
 * /shareholder/dashboard — read-only investor view.
 *
 * What external shareholders see:
 *   • Their own row(s) — name, equity %, contribution, instrument type, terms
 *   • Their Y1 dividend projection
 *   • Their ROI vs contribution
 *   • No other shareholders' data, no Finance Model knobs, no scenarios
 *
 * Backed by: GET /api/shareholder/me  (auto-filters to user_email == auth.email)
 *
 * Access: any authenticated user. The endpoint itself enforces the filter.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, LogOut, ShieldCheck, TrendingUp, Coins, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtQAR(n) {
    if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
    return `QAR ${Number(n).toLocaleString("en", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const INSTRUMENT_BADGE = {
    founder: { label: "Founder shares", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
    equity: { label: "Priced equity", cls: "bg-indigo-100 text-indigo-800 border-indigo-200" },
    safe_note: { label: "SAFE note", cls: "bg-amber-100 text-amber-800 border-amber-200" },
};

export default function ShareholderDashboard() {
    const { user, logout, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);

    useEffect(() => {
        if (authLoading) return;
        if (!user) {
            navigate("/login?next=/shareholder/dashboard", { replace: true });
            return;
        }
        let alive = true;
        (async () => {
            try {
                const { data } = await axios.get(`${API}/shareholder/me`);
                if (alive) setData(data);
            } catch (e) {
                if (alive) setErr(e?.response?.data?.detail || "Could not load your shareholder dashboard.");
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [user, authLoading, navigate]);

    if (authLoading || loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50">
                <Loader2 className="animate-spin text-violet-600" size={32} />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-violet-50/30" data-testid="shareholder-dashboard">
            {/* Top bar */}
            <div className="bg-slate-900 text-white">
                <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="font-display text-xl tracking-tight">Toothmatrix</div>
                        <span className="text-[10px] uppercase tracking-[0.2em] text-cyan-300 font-mono-tr">Shareholder dashboard</span>
                    </div>
                    <div className="flex items-center gap-3 text-[12.5px]">
                        <span className="text-slate-300">{user?.email}</span>
                        <button
                            onClick={async () => { await logout(); navigate("/login"); }}
                            data-testid="shareholder-logout"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-slate-800 hover:bg-slate-700 text-white text-[12px]"
                        >
                            <LogOut size={12} /> Sign out
                        </button>
                    </div>
                </div>
            </div>

            <div className="max-w-5xl mx-auto px-6 py-10">
                <h1 className="font-display text-4xl text-slate-900 tracking-tight">Your stake at a glance</h1>
                <p className="text-slate-600 mt-1 text-[14px]">
                    Welcome{user?.name ? `, ${user.name}` : ""} — this is a read-only view of your shareholding.
                    Numbers refresh whenever the founder updates the cap table or operating model.
                </p>

                {err && (
                    <div className="mt-6 rounded-xl border border-rose-200 bg-rose-50 p-4 flex items-start gap-2 text-rose-800 text-[13px]" data-testid="shareholder-error">
                        <AlertCircle size={16} className="shrink-0 mt-0.5" />
                        <div>{err}</div>
                    </div>
                )}

                {!err && data?.rows?.length === 0 && (
                    <div className="mt-8 rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center" data-testid="shareholder-empty">
                        <ShieldCheck size={32} className="mx-auto text-slate-400 mb-3" />
                        <div className="font-display text-xl text-slate-700">No shareholding linked to your account yet</div>
                        <div className="text-[13px] text-slate-500 mt-2 max-w-md mx-auto">
                            {data?.message || "Ask the founder to link your email to your shareholder row. Once they do, your dividend projection will appear here."}
                        </div>
                    </div>
                )}

                {!err && data?.rows?.length > 0 && (
                    <>
                        {data?.sim_summary && (
                            <div className="mt-6 rounded-xl border border-slate-200 bg-white p-4 flex items-center gap-3 text-[12.5px] text-slate-700">
                                <Coins size={16} className="text-violet-600" />
                                <span>
                                    Total Y{data.sim_summary.year} distributable across the cap table: <b>{fmtQAR(data.sim_summary.annual_distributable_qar)}</b>.
                                    Your slice is a function of your equity % below.
                                </span>
                            </div>
                        )}

                        <div className="mt-6 space-y-5" data-testid="shareholder-rows">
                            {data.rows.map((row) => {
                                const inst = INSTRUMENT_BADGE[row.instrument_type] || INSTRUMENT_BADGE.equity;
                                return (
                                    <div key={row.id} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid={`shareholder-row-${row.id}`}>
                                        <div className="flex items-start justify-between flex-wrap gap-3">
                                            <div>
                                                <div className="font-display text-2xl text-slate-900">{row.name}</div>
                                                {row.note && <div className="text-[12.5px] text-slate-500 mt-1 italic">{row.note}</div>}
                                            </div>
                                            <span className={`inline-block px-3 py-1 rounded-full text-[11px] font-mono-tr uppercase tracking-wider border ${inst.cls}`}>
                                                {inst.label}
                                            </span>
                                        </div>

                                        {/* KPI tiles */}
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
                                            <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3">
                                                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono-tr">Equity</div>
                                                <div className="text-2xl font-display tabular-nums mt-1">{(row.equity_pct || 0).toFixed(2)}%</div>
                                            </div>
                                            <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3">
                                                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono-tr">Contribution</div>
                                                <div className="text-xl font-display tabular-nums mt-1">{fmtQAR(row.invested_amount)}</div>
                                            </div>
                                            <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-3">
                                                <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-mono-tr">Y1 dividend</div>
                                                <div className="text-xl font-display tabular-nums mt-1 text-emerald-900">{fmtQAR(row.y1_dividend_projection_qar)}</div>
                                                <div className="text-[10px] text-emerald-700/70 mt-0.5">projected</div>
                                            </div>
                                            <div className="rounded-xl border border-violet-200 bg-violet-50/40 p-3">
                                                <div className="text-[10px] uppercase tracking-wider text-violet-700 font-mono-tr">ROI Y1</div>
                                                <div className="text-xl font-display tabular-nums mt-1 text-violet-900">{row.roi_pct_year_1 === null ? "—" : `${row.roi_pct_year_1}%`}</div>
                                                <div className="text-[10px] text-violet-700/70 mt-0.5">on contribution</div>
                                            </div>
                                        </div>

                                        {/* Terms breakdown */}
                                        <div className="mt-5 rounded-xl border border-slate-200 bg-white p-3.5 grid grid-cols-2 md:grid-cols-3 gap-3 text-[12.5px]">
                                            <div>
                                                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono-tr">Instrument</div>
                                                <div className="mt-0.5 font-medium">{inst.label}</div>
                                            </div>
                                            {row.instrument_type === "equity" && (
                                                <div>
                                                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono-tr">Premium paid</div>
                                                    <div className="mt-0.5 tabular-nums">+{(row.discount_or_premium_pct || 0).toFixed(1)}% over par</div>
                                                </div>
                                            )}
                                            {row.instrument_type === "safe_note" && (
                                                <>
                                                    <div>
                                                        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono-tr">Discount at conversion</div>
                                                        <div className="mt-0.5 tabular-nums">{(row.discount_or_premium_pct || 0).toFixed(1)}%</div>
                                                    </div>
                                                    {row.valuation_cap_qar > 0 && (
                                                        <div>
                                                            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono-tr">Valuation cap</div>
                                                            <div className="mt-0.5 tabular-nums">{fmtQAR(row.valuation_cap_qar)}</div>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                            {row.instrument_type === "founder" && (
                                                <div>
                                                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono-tr">Terms</div>
                                                    <div className="mt-0.5 italic text-slate-500">Founding shares</div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Disclaimer */}
                        <div className="mt-8 rounded-xl border border-slate-200 bg-white p-4 text-[11.5px] text-slate-500 leading-relaxed flex items-start gap-2" data-testid="shareholder-disclaimer">
                            <TrendingUp size={14} className="shrink-0 mt-0.5 text-slate-400" />
                            <div>
                                Forward-looking projections derived from the founder&apos;s current operating model. Dividend payouts only occur on months with positive net cash; loss months are absorbed by retained earnings, not distributed. Figures are not a guarantee — they update as the lab&apos;s actuals and the founder&apos;s slider knobs change.
                            </div>
                        </div>
                    </>
                )}
            </div>

            <div className="max-w-5xl mx-auto px-6 pb-8 text-center text-[11px] text-slate-400">
                Questions? <Link to="/contact" className="text-violet-700 hover:text-violet-900 underline underline-offset-2">Contact the founder</Link>.
            </div>
        </div>
    );
}
